import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation, Outlet, NavLink } from "react-router-dom";
import Layout from "../../components/Layout";
import config from "../../config";
import { PendingBox } from "./Pending";
import "./ReportShell.css";

const TABS = [
  { path: "", label: "Overview" },
  { path: "prompts", label: "Prompts" },
  { path: "responses", label: "Responses" },
  { path: "competitors", label: "Competitors" },
  { path: "sources", label: "Sources" },
  { path: "sentiment", label: "Sentiment" },
  { path: "settings", label: "Settings" },
];

export default function ReportShell() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true); // first load only; refreshes keep the page as is
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState("");
  const [exportRun, setExportRun] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [saEmail, setSaEmail] = useState(null);

  const fetchReport = useCallback(async () => {
    try {
      const r = await fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}`, {
        credentials: "include",
      });
      const d = await r.json();
      if (d.success) { setReport(d.report); setSaEmail(d.exportServiceAccountEmail || null); }
      else setError(d.error || "Report not found.");
    } catch (e) {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const completedRuns = useMemo(
    () => (report?.runs || [])
      .filter((r) => r.status === "completed")
      .sort((a, b) => b.runNumber - a.runNumber),
    [report],
  );

  useEffect(() => {
    if (exportRun == null && completedRuns.length > 0) {
      setExportRun(completedRuns[0].runNumber);
    }
  }, [completedRuns, exportRun]);

  const handleRunNow = async () => {
    setIsRunning(true);
    try {
      const r = await fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}/run-now`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json();
      if (!d.success) setError(d.error || "Could not start run.");
      else setTimeout(fetchReport, 1500);
    } catch (e) {
      setError("Network error.");
    } finally {
      setIsRunning(false);
    }
  };

  const handlePauseToggle = async () => {
    if (!report) return;
    const nextPaused = !(report.schedule?.paused);
    try {
      const r = await fetch(`${config.api.baseUrl}/gpt-seo/reports/${reportId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ schedule: { paused: nextPaused } }),
      });
      const d = await r.json();
      if (d.success) setReport(d.report);
    } catch (e) {
      setError("Network error.");
    }
  };

  const handleExport = async () => {
    if (exportRun == null) return;
    setExporting(true);
    setError("");
    try {
      const res = await fetch(
        `${config.api.baseUrl}/gpt-seo/reports/${reportId}/export?run=${exportRun}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        let msg = "Export failed.";
        try { const d = await res.json(); msg = d.error || msg; } catch (_) { /* non-JSON */ }
        setError(msg);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `geo-report-run${exportRun}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("Network error.");
    } finally {
      setExporting(false);
    }
  };

  if (!loading && !report) {
    return (
      <Layout>
        <div className="geo-report-shell geo-report-shell--message">
          {error || "Report not found."}
        </div>
      </Layout>
    );
  }
  if (report && report.schemaVersion !== 2) {
    return (
      <Layout>
        <div className="geo-report-shell geo-report-shell--message">
          <h2>Legacy report</h2>
          <p>This report was created with the old keyword-based system and isn&apos;t viewable in the new dashboard.</p>
          <button className="geo-btn geo-btn-primary" onClick={() => navigate("/gpt-seo-tracking")}>
            Back to GEO Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  // The list page passes the client name through router state so the sidebar
  // can paint it before the report doc arrives.
  const displayName = report?.clientName || location.state?.clientName || "";
  const domainsLabel = report
    ? (report.clientDomains?.length ? report.clientDomains : [report.clientDomain]).filter(Boolean).join(", ")
    : "";
  const nextRunLabel = !report ? "" : report.schedule?.paused
    ? "Paused"
    : report.schedule?.nextRunAt
      ? `Next: ${new Date(report.schedule.nextRunAt).toLocaleString()}`
      : "Unscheduled";
  const statusLabel = report?.status === "running" ? "Running" : report?.status;

  return (
    <Layout>
      <div className="geo-report-shell">
        <aside className="geo-sidebar">
          <button className="geo-sidebar-back" onClick={() => navigate("/gpt-seo-tracking")}>
            ← All reports
          </button>

          <div className="geo-sidebar-client">
            <div className="geo-client-name" title={displayName}>
              {displayName || <span className="muted">Loading report…</span>}
            </div>
            {domainsLabel && (
              <div className="geo-client-domain" title={domainsLabel}>
                {domainsLabel}
              </div>
            )}
          </div>

          {report && (
            <div className="geo-sidebar-status">
              <span className={`geo-status-pill geo-status-${report.status}`}>{statusLabel}</span>
              <div className="geo-next-run" title={nextRunLabel}>{nextRunLabel}</div>
            </div>
          )}

          <div className="geo-sidebar-actions">
            <button
              className="geo-btn geo-btn-primary"
              disabled={!report || isRunning || report.status === "running"}
              onClick={handleRunNow}
            >
              {report?.status === "running" ? "Running…" : "Run now"}
            </button>
            <button className="geo-btn geo-btn-secondary" onClick={handlePauseToggle} disabled={!report}>
              {report?.schedule?.paused ? "Resume" : "Pause"}
            </button>
            <div className="geo-export">
              <select
                className="geo-export-run"
                value={exportRun ?? ""}
                onChange={(e) => setExportRun(Number(e.target.value))}
                disabled={completedRuns.length === 0 || exporting}
                aria-label="Run to export"
              >
                {completedRuns.length === 0 && <option value="">No runs</option>}
                {completedRuns.map((r) => (
                  <option key={r.runNumber} value={r.runNumber}>
                    Run #{r.runNumber}
                    {r.completedAt ? ` · ${new Date(r.completedAt).toLocaleDateString()}` : ""}
                  </option>
                ))}
              </select>
              <button
                className="geo-btn geo-btn-secondary"
                onClick={handleExport}
                disabled={completedRuns.length === 0 || exporting || exportRun == null}
              >
                {exporting ? "Exporting…" : "Export to Excel"}
              </button>
            </div>
          </div>

          <nav className="geo-sidebar-nav" aria-label="Report sections">
            {TABS.map((t) => (
              <NavLink
                key={t.path}
                end={t.path === ""}
                to={`/gpt-seo-tracking/reports/${reportId}${t.path ? `/${t.path}` : ""}`}
                className={({ isActive }) => `geo-nav-item${isActive ? " geo-nav-item--active" : ""}`}
              >
                {t.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="geo-main" role="main">
          {error && report && <div className="error-message">{error}</div>}
          {report
            ? <Outlet context={{ report, reportId, refresh: fetchReport, exportServiceAccountEmail: saEmail }} />
            : <PendingBox height={320} />}
        </main>
      </div>
    </Layout>
  );
}
